import { Grid } from "@material-ui/core";
import moment from "moment";
import { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Option } from "space-monad";
import styled from "styled-components";
import { socialIconMap } from "../../../components/icons/social";
import { WrapGridCell } from "../../../components/layout";
import { Table } from "../../../components/table";
import { Widget } from "../../../components/widget";
import { validationFields } from "../../../store/common/models/fields";
import { CryptoDTO } from "../../../store/crypto/models/dto";
import {
  ReportingOptionEnum,
  TokenLifecycleEnum,
  TypeOfFraudEnum,
} from "../../../store/crypto/models/internal";

const StyledWidget = styled(Widget)`
  &:not(:last-child) {
    margin-bottom: 1em;
  }
`;

const Label = styled.span`
  font-weight: 500;
`;

const SocialIcon = styled.div`
  height: 1.5em;
`;

export const SingleFraudReport: FunctionComponent<{
  fraudCheckId: string;
  item: CryptoDTO.Responses.GetSingleFraudReport.T;
}> = ({ item }) => {
  const {
    action,
    address,
    attachments,
    blockchain,
    createdAt,
    description,
    fraudAmount,
    fraudCurrency,
    fraudType,
    reportType,
    socialMedia,
    tokenLifecycle,
    transactions,
  } = item;

  const formattedDate = useMemo(
    () => moment.utc(createdAt).local().format("DD-MM-yyyy HH:mm"),
    [createdAt]
  );

  const { t } = useTranslation("blockchain-tools");

  const actionName = validationFields.action.useName();
  const descriptionName = validationFields.description.useName();
  const fraudTypeName = validationFields.fraudType.useName();
  const reportTypeName = validationFields.reportType.useName();
  const blockchainName = validationFields.blockchain.useName();
  const fraudAmountName = validationFields.fraudAmount.useName();
  const addressName = validationFields.address.useName();
  const tokenLifecycleName = validationFields.tokenLifecycle.useName();

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <StyledWidget title={t("overview")}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Label>{actionName}:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    {action}
                  </Grid>
                  <Grid item xs={6}>
                    <Label>{descriptionName}:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    {description}
                  </Grid>
                  <Grid item xs={6}>
                    <Label>{fraudTypeName}:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    {TypeOfFraudEnum.enum[fraudType]}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Label>{t("date-submitted")}:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    {formattedDate}
                  </Grid>
                  <Grid item xs={6}>
                    <Label>{reportTypeName}:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    {ReportingOptionEnum.enum[reportType]}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </StyledWidget>
          <StyledWidget title={t("more-details")}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Label>{blockchainName}:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    {blockchain}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Label>{fraudAmountName}:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    {fraudCurrency}{" "}
                    {fraudAmount
                      .toString()
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={3}>
                <Label>{addressName}:</Label>
              </Grid>
              <WrapGridCell item xs={9}>
                {address}
              </WrapGridCell>
              <Grid item xs={6}>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Label>{tokenLifecycleName}:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    {TokenLifecycleEnum.enum[tokenLifecycle]}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </StyledWidget>
          {Option(socialMedia)
            .filter((x) => x.length > 0)
            .fold(
              () => null,
              (data) => (
                <StyledWidget title={t("social-information")}>
                  <Table
                    data={data}
                    head={[
                      { name: "type", title: t("platform") },
                      { name: "value", title: t("info") },
                    ]}
                    renderFields={{
                      type: ({ type }) => (
                        <SocialIcon as={socialIconMap[type]} />
                      ),
                    }}
                  ></Table>
                </StyledWidget>
              )
            )}
          {Option(transactions)
            .filter((x) => x.length > 0)
            .fold(
              () => null,
              (data) => (
                <StyledWidget title={t("transactions", { ns: "fields" })}>
                  <Table
                    data={data}
                    head={[
                      {
                        name: "reference",
                        title: t("reference", { ns: "fields" }),
                      },
                      {
                        name: "transactionDate",
                        title: t("date", { context: "title", ns: "common" }),
                      },
                    ]}
                  ></Table>
                </StyledWidget>
              )
            )}
          {Option(attachments)
            .filter((x) => x.length > 0)
            .fold(
              () => null,
              (data) => (
                <StyledWidget title={t("attachments")}>
                  <Table
                    data={data}
                    head={[
                      { name: "id", title: t("id", { ns: "fields" }) },
                      {
                        name: "attachedDate",
                        title: t("date", { context: "title", ns: "common" }),
                      },
                    ]}
                    renderFields={{
                      attachedDate: ({ attachedDate }) => (
                        <>
                          {moment
                            .utc(attachedDate)
                            .local()
                            .format("DD-MM-yyyy HH:mm")}
                        </>
                      ),
                    }}
                  ></Table>
                </StyledWidget>
              )
            )}
        </Grid>
      </Grid>
    </>
  );
};
